import React, { useEffect, useRef, useState } from 'react';
import TCClient from '../../../../client';
import { DotsIndicator } from '../../../../common/components';
import Modal from '../../../../common/components/Modal';
import { Input } from '../../../shared-components/Input/Input';

import styles from './styles.module.css';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  mode:
    | {
        type: 'edit';
        data: { sectionName: string; parentRoomId: string };
      }
    | {
        type: 'create';
        data: { parentRoomId: string };
      }
    | {
        type: 'delete';
        data: { sectionName: string; parentRoomId: string };
      };
  openParent: (id: string, open: boolean) => void;
};

export const SectionModal = ({ isOpen, mode, onClose, openParent }: Props) => {
  const dispatch = useAppDispatch();
  const selectedOrganizationId = useAppSelector((state) => state.admin.selectedOrganizationId);
  const isDeleteMode = mode.type === 'delete';
  const isEditMode = mode.type === 'edit';
  const isCreateMode = mode.type === 'create';
  const _sectionName = isEditMode ? mode.data.sectionName : '';
  const [sectionName, setSectionName] = useState(_sectionName);

  const header = isEditMode
    ? `Edit Section`
    : isDeleteMode
    ? 'Delete Section'
    : `Create new Section`;

  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [buttonHasFocus, setButtonHasFocus] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const handleMouseEnter = () => setButtonHasFocus(true);
  const handleMouseLeave = () => setButtonHasFocus(false);

  let formHasChanged = false;
  if (isEditMode) {
    formHasChanged = mode.data.sectionName !== sectionName;
  } else {
    formHasChanged = sectionName !== '';
  }

  useEffect(() => {
    let ref: HTMLButtonElement | null = null;

    buttonRef.current?.addEventListener('mouseenter', handleMouseEnter);
    buttonRef.current?.addEventListener('mouseleave', handleMouseLeave);

    ref = buttonRef.current;

    return () => {
      if (ref) {
        ref.removeEventListener('mouseenter', handleMouseEnter);
        ref.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  });

  const close = () => {
    setSectionName(_sectionName);
    setError('');
    onClose();
  };

  return (
    <Modal
      bodyClass={styles.body}
      closeClass={isDeleteMode ? styles.close : ''}
      headerClass={isDeleteMode ? styles.header : ''}
      header={header}
      isOpen={isOpen}
      size={'medium'}
      onRequestClose={close}
    >
      <>
        {(isEditMode || isCreateMode) && (
          <div>
            <div className={styles.errorContainer}>
              {error.length > 0 && <div className={styles.error}>{error}</div>}
            </div>
            <div className={styles.inputContainer}>
              <span className={styles.inputLabel}>Section Name</span>
              <Input
                customCSS={styles.input}
                lengthLimit={100}
                onInputChange={(e) => setSectionName(e.target.value)}
                placeholder={`Type a section name...`}
                value={sectionName}
              />
            </div>
          </div>
        )}
        {isDeleteMode && (
          <div className={styles.bodyText}>
            <p>
              Are you sure you want to remove{' '}
              <span className={styles.bold}>{mode.data.sectionName}?</span>
            </p>
          </div>
        )}
        <div className={styles.footer}>
          <div className={styles.footerLeft}></div>
          <div className={styles.footerRight}>
            <button
              aria-label="Cancel"
              className={styles.cancelButton}
              onClick={close}
              type="button"
            >
              Cancel
            </button>
            <button
              ref={buttonRef}
              disabled={!isDeleteMode ? !formHasChanged : false}
              aria-label={isDeleteMode ? 'Delete' : 'Save'}
              className={isDeleteMode ? styles.deleteButton : styles.saveButton}
              onClick={async () => {
                try {
                  setIsSaving(true);
                  if (isEditMode) {
                    await TCClient.multiOrg.updateSection({
                      id: mode.data.parentRoomId,
                      orgId: selectedOrganizationId,
                      sectionName,
                    });
                  } else if (isCreateMode) {
                    await TCClient.multiOrg.createSection({
                      orgId: selectedOrganizationId,
                      sectionName,
                      parentRoomId: mode.data.parentRoomId,
                    });
                    openParent(mode.data.parentRoomId, false);
                  } else {
                    await TCClient.multiOrg.deleteSection({
                      id: mode.data.parentRoomId,
                      orgId: selectedOrganizationId,
                    });
                  }
                  setIsSaving(false);
                  dispatch(actions.setLocationsDataDirty({ isLocationsDataDirty: true }));
                  setSectionName(sectionName);
                  setError('');
                  close();
                } catch (e) {
                  if (
                    e.text &&
                    typeof e.text === 'string' &&
                    e.text.includes('Invalid input for name.')
                  ) {
                    setError(`This Section name is already in use`);
                  } else {
                    setError('Oops! Something went wrong, please try again');
                  }
                  setIsSaving(false);
                }
              }}
              type="button"
            >
              {isSaving ? (
                <DotsIndicator color={buttonHasFocus ? '#fff' : '#4580d8'} size={10} speed={0.5} />
              ) : isDeleteMode ? (
                'Delete'
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </>
    </Modal>
  );
};
